// "Stop for the day": a banner across the app when an account has used 75% or more of today's
// daily loss limit, or has less than 25% of its max loss room left (accountStatus, balance.js).
// The phone alert for the same moment is limitAlerts.js on the server. Admin only for now (gold).
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { accountStatus } from './balance';
import { useI18n } from './i18n';
import { currentTradingDay, usd } from './pnl';

const key = (id, kind) => `mm-limit-${id}-${kind}-${currentTradingDay()}`;
const hidden = (id, kind) => { try { return localStorage.getItem(key(id, kind)) === '1'; } catch { return false; } };

// The accounts to warn about now: { account, kind: 'daily' | 'dailyHit' | 'maxLoss', used, loss, limit, left }.
export function limitWarnings(accounts, trades) {
  const out = [];
  for (const a of accounts) {
    if (a.archived || a.attemptOf || !(a.dailyLossLimit || a.maxLossLimit)) continue;
    const s = accountStatus(a, trades.filter((x) => String(x.accountId) === String(a._id)));
    if (!s || s.risk === 'blown') continue;
    if (a.dailyLossLimit && s.dailyLeft !== undefined) {
      const loss = a.dailyLossLimit - s.dailyLeft;
      const used = loss / a.dailyLossLimit;
      if (used >= 0.75) { out.push({ account: a, kind: used >= 1 ? 'dailyHit' : 'daily', used, loss, limit: a.dailyLossLimit }); continue; }
    }
    if (a.maxLossLimit && s.toBlow !== undefined && s.toBlow > 0 && s.toBlow < a.maxLossLimit * 0.25) out.push({ account: a, kind: 'maxLoss', left: s.toBlow, limit: a.maxLossLimit });
  }
  return out;
}

export default function LimitBanner({ user, accounts, trades }) {
  const { t } = useI18n();
  const [, setTick] = useState(0);
  const warnings = useMemo(() => (user && user.isAdmin && !user.isDemo ? limitWarnings(accounts, trades) : []), [user, accounts, trades]);
  const shown = warnings.filter((w) => !hidden(w.account._id, w.kind));
  if (!shown.length) return null;
  return (
    <div className="limit-banners">
      {shown.map((w) => (
        <div key={`${w.account._id}-${w.kind}`} className={`limit-banner ${w.kind === 'daily' ? 'warn' : 'stop'} admin-preview-box`} role="alert">
          <span aria-hidden="true">{w.kind === 'daily' ? '⚠️' : '🛑'}</span>
          <p>
            <strong>{w.kind === 'dailyHit' ? t('Daily loss limit reached') : t('Stop for the day?')}</strong>{' '}
            {w.kind === 'daily' && t('{name} has used {pct}% of today’s daily loss ({loss} of {limit}). One more loss and the day is over.', { name: w.account.name, pct: Math.round(w.used * 100), loss: usd.format(w.loss), limit: usd.format(w.limit) })}
            {w.kind === 'dailyHit' && t('{name} is down {loss} today, its {limit} limit. You’re done for today.', { name: w.account.name, loss: usd.format(w.loss), limit: usd.format(w.limit) })}
            {w.kind === 'maxLoss' && t('{name} has only {left} left before its max loss. Trade small or stop until you have a plan.', { name: w.account.name, left: usd.format(w.left) })}
            {' '}<span className="admin-preview-badge">{t('Admin only')}</span>
          </p>
          <Link to="/app/accounts" className="link-button">{t('Accounts')}</Link>
          <button type="button" className="icon-button" aria-label={t('Hide for today')} title={t('Hide for today')}
            onClick={() => { try { localStorage.setItem(key(w.account._id, w.kind), '1'); } catch { /* shown again */ } setTick((n) => n + 1); }}>×</button>
        </div>
      ))}
    </div>
  );
}
